import { useRef, useEffect, useState } from 'react'
import * as rive from '@rive-app/canvas'
import { useInView } from 'framer-motion'

export enum RiveFit {
  HEIGHT = rive.Fit.FitHeight,
  WIDTH = rive.Fit.FitWidth,
  FILL = rive.Fit.Fill,
  CONTAIN = rive.Fit.Contain
}

interface GenericRiveComponentProps {
  src: string
  stateMachine?: string
  riveFit?: `${RiveFit}`
  isLoop?: boolean
  className?: string
  onLoad?: () => void
  onPlay?: () => void
  onStop?: () => void
  onLoop?: () => void
  autoPlay?: boolean
}

const GenericRiveComponent = ({
  src,
  stateMachine = 'State Machine 1',
  riveFit = RiveFit.CONTAIN,
  isLoop = true,
  className = 'w-full h-full',
  onLoad,
  onPlay,
  onStop,
  onLoop,
  autoPlay = true
}: GenericRiveComponentProps) => {
  const riveInstance = useRef<rive.Rive | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const isInView = useInView(canvasRef, { once: true, amount: 0.5 })

  function redrawRive() {
    if (riveInstance && riveInstance.current) {
      riveInstance.current.resizeDrawingSurfaceToCanvas()
    }
  }

  function createRiveInstance() {
    riveInstance.current = new rive.Rive({
      src,
      canvas: canvasRef?.current as HTMLCanvasElement,
      stateMachines: stateMachine,
      onPlay: () => {
        onPlay?.()
      },
      onLoop: () => {
        if (!isLoop) {
          riveInstance.current?.stop()
        }
        onLoop?.()
      },
      onStop: () => {
        onStop?.()
      },
      onLoad: (event) => {
        redrawRive()
        onLoad?.()
      }
    })

    riveInstance.current.layout = new rive.Layout({ fit: riveFit as any })
  }

  useEffect(() => {
    if (riveInstance.current) {
      riveInstance.current.cleanup()
    }
    createRiveInstance()
    redrawRive()
    
    if (autoPlay) {
      setTimeout(() => {
        if (riveInstance.current) {
          riveInstance.current.play()
        }
      }, 0)
    }
  }, [src, stateMachine])

  useEffect(() => {
    window.addEventListener('resize', redrawRive, false)
    return () => {
      window.removeEventListener('resize', redrawRive, false)
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (riveInstance.current) {
        riveInstance.current.cleanup()
      }
    }
  }, [])

  return <canvas ref={canvasRef} className={className} />
}

export default GenericRiveComponent 